import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sprout, Sun, Search, PackageCheck, Plane, UtensilsCrossed, ArrowRight, Leaf, ShieldCheck, Star } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';
import { FadeUp, FadeIn, StaggerContainer, StaggerItem } from '../components/ScrollReveal';

const HERO_IMAGE = 'https://customer-assets.emergentagent.com/job_bb23729c-0dea-4683-8280-e0aa97a36711/artifacts/rpuw479q_IMG_2753.jpeg';
const HERO_IMAGE_2 = 'https://customer-assets.emergentagent.com/job_bb23729c-0dea-4683-8280-e0aa97a36711/artifacts/9f2ikqv7_IMG_2754.jpeg';
const GALLERY_1 = 'https://customer-assets.emergentagent.com/job_bb23729c-0dea-4683-8280-e0aa97a36711/artifacts/m3fyvruc_IMG_2764.avif';
const GALLERY_2 = 'https://images.pexels.com/photos/14381803/pexels-photo-14381803.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';
const GALLERY_3 = 'https://images.pexels.com/photos/14381802/pexels-photo-14381802.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';
const ORCHID_IMG = 'https://images.unsplash.com/photo-1713324625715-8e121584cd3e?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85';
const GREEN_VANILLA_IMG = 'https://i.pinimg.com/1200x/82/a0/4e/82a04eb584afbdb3e72bd0b3f1eb1b35.jpg';

const Home = () => {
  const { lang } = useLanguage();
  const t = translations[lang].home;

  const customTexts = {
    fr: {
      heroP1: "VANELYS est une société de distribution indépendante de vanille premium basée à Nice. Nous travaillons directement avec des producteurs sélectionnés pour leur qualité et leur traçabilité.",
      heroP2: "Nous intervenons aussi dans le courtage de matières premières. Étant mandataires pour des organismes actifs dans le cacao et ses dérivés et la noix de cajou brute, nous intervenons aussi dans le courtage de matières premières.",
      agriTitle: "Agriculture durable",
      agriDesc: "Partenariats durables avec des agriculteurs et entreprises engagés dans une production et une transformation responsable.",
      missionTitle: "L'excellence"
    },
    en: {
      heroP1: "VANELYS is an independent premium vanilla distribution company based in Nice. We work directly with producers selected for their quality and traceability.",
      heroP2: "We are also active in commodities brokerage. Acting as agents for organizations active in cocoa and its derivatives and raw cashew nuts, we also operate in raw materials brokerage.",
      agriTitle: "Sustainable Agriculture",
      agriDesc: "Sustainable partnerships with farmers and companies committed to responsible production and processing.",
      missionTitle: "Excellence"
    }
  };

  const current = customTexts[lang] || customTexts.fr;

  const strengths = [
    { icon: Sprout, title: t.s1Title, desc: t.s1Desc },
    { icon: ShieldCheck, title: t.s2Title, desc: t.s2Desc },
    { icon: Star, title: t.s3Title, desc: t.s3Desc },
    { icon: Leaf, title: current.agriTitle, desc: current.agriDesc },
  ];

  const chainSteps = [
    { icon: Sprout, label: t.chainStep1 },
    { icon: Sun, label: t.chainStep2 },
    { icon: PackageCheck, label: t.chainStep3 },
    { icon: Search, label: t.chainStep4 },
    { icon: Plane, label: t.chainStep5 },
    { icon: UtensilsCrossed, label: t.chainStep6 },
  ];

  return (
    <div>
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden py-32">
        <div className="absolute inset-0 z-0">
          <img src={HERO_IMAGE} alt="Premium vanilla beans" className="w-full h-full object-cover animate-ken-burns" />
          <div className="absolute inset-0 bg-[#2C2416]/55" />
        </div>

        <div className="relative z-10 text-center px-4 w-full max-w-5xl mx-auto flex flex-col items-center">


          <motion.h1 initial={{ opacity: 0, y: 25 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.35 }}
            style={{ fontFamily: '"Cormorant Garamond", serif', fontStyle: 'italic' }}
            className="text-5xl md:text-7xl lg:text-8xl font-light text-white leading-[1.08] mb-6">
            {t.heroTitle}<br /><em className="not-italic" style={{ color: '#E8D5A0' }}>{t.heroTitleAccent}</em>
          </motion.h1>

          {/* AJOUT DE w-full et px-4 ICI POUR ÉVITER LE COMPRESSAGE DU TEXTE */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.6 }}
            className="w-full max-w-4xl mx-auto px-4 text-white/80 text-base md:text-lg font-light leading-relaxed mb-12 space-y-5">
            <p>{current.heroP1}</p>
            <p>{current.heroP2}</p>
          </motion.div>

          <div className="flex flex-col sm:flex-row flex-wrap gap-4 justify-center w-full max-w-xs sm:max-w-none mx-auto mb-8 px-4">
            <Link to="/quote" className="btn-secondary w-full sm:w-auto text-center">{t.ctaQuote}</Link>
            <Link to="/contact" className="text-white/80 hover:text-white border border-white/30 hover:border-white/60 rounded-full px-8 py-3.5 text-xs uppercase tracking-[0.18em] transition-all hover:scale-105 w-full sm:w-auto text-center">
              {t.ctaContact}
            </Link>
          </div>
        </div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3 }}
          className="absolute bottom-6 left-1/2 -translate-x-1/2">
          <div className="w-px h-12 bg-gradient-to-b from-white/0 to-white/40 mx-auto" />
        </motion.div>
      </section>

      {/* SECTION ENGAGEMENTS */}
      <section className="section-padding bg-[#FAF7F2]">
        <div className="container-max">
          <FadeUp>
            <p className="section-overline text-center">{t.strengthsOverline}</p>
            <h2 className="text-4xl md:text-5xl font-serif text-center text-[#2C2416] mb-4" style={{ fontFamily: '"Cormorant Garamond", serif', fontStyle: 'italic' }}>
              {t.strengthsTitle}
            </h2>
            <div className="gold-divider mx-auto" />
          </FadeUp>
          <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mt-14">
            {strengths.map(({ icon: Icon, title, desc }, i) => (
              <StaggerItem key={i}>
                <div className="flex flex-col items-center text-center group">
                  <div className="icon-badge mb-5 group-hover:border-[#8B4513]/40 transition-all"><Icon size={20} className="text-[#8B4513]" /></div>
                  <h3 className="text-base font-semibold text-[#2C2416] mb-2">{title}</h3>
                  <p className="text-[#7A6E63] text-sm leading-relaxed">{desc}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* SECTION CHAINE LOGISTIQUE */}
      <section className="section-padding bg-[#2B2016]">
        <div className="container-max text-white text-center">
          <h2 className="text-4xl mb-12" style={{ fontFamily: '"Cormorant Garamond", serif', fontStyle: 'italic' }}>{t.chainTitle}</h2>
          <div className="flex flex-wrap justify-center gap-8">
            {chainSteps.map((step, i) => (
              <div key={i} className="flex flex-col items-center gap-2">
                <div className="w-12 h-12 rounded-full border border-[#C9A84C]/40 flex items-center justify-center"><step.icon size={18} className="text-[#C9A84C]" /></div>
                <span className="text-[10px] uppercase tracking-wider opacity-60">{step.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION SÉLECTION */}
      <section className="section-padding bg-[#F0EBE0]">
        <div className="container-max">
          <FadeUp>
            <h2 className="text-4xl md:text-5xl text-center text-[#2C2416] mb-12" style={{ fontFamily: '"Cormorant Garamond", serif', fontStyle: 'italic' }}>{t.galleryTitle}</h2>
          </FadeUp>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {[HERO_IMAGE_2, GALLERY_1, GALLERY_2, GALLERY_3, ORCHID_IMG, GREEN_VANILLA_IMG].map((img, i) => (
              <FadeIn key={i} delay={i * 0.1} className="rounded-2xl overflow-hidden">
                <img src={img} alt="Vanilla Selection" className="w-full h-full min-h-[180px] object-cover hover:scale-105 transition-transform duration-700" />
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION MISSION */}
      <section className="section-padding bg-[#2B2016] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10"><img src={HERO_IMAGE} alt="" className="w-full h-full object-cover" /></div>
        <div className="relative container-max text-center max-w-3xl mx-auto">
          <FadeUp>
            <h2 className="text-4xl md:text-6xl text-white mb-5" style={{ fontFamily: '"Cormorant Garamond", serif', fontStyle: 'italic' }}>
              {current.missionTitle}
            </h2>
            <div className="w-10 h-px bg-[#C9A84C] mx-auto my-5" />
            <p className="text-white/60 text-base md:text-lg font-light leading-relaxed mb-10">{t.missionDesc}</p>
            <Link to="/products" className="btn-secondary">{t.missionCta} <ArrowRight size={14} /></Link>
          </FadeUp>
        </div>
      </section>
    </div>
  );
};

export default Home;